import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, AuthCard, BrandIcon } from "@/components/AuthShell";
import { InstallAppButton } from "@/components/InstallAppButton";
import { toast } from "sonner";

const REMEMBER_KEY = "mj_remember_email";

export default function Login() {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [mode, setMode] = useState("password");
  const [form, setForm] = useState({ email: "", password: "", otp: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) { setForm((f) => ({ ...f, email: saved })); setRemember(true); }
  }, []);

  const persistEmail = () => {
    if (remember && form.email) localStorage.setItem(REMEMBER_KEY, form.email);
    else localStorage.removeItem(REMEMBER_KEY);
  };

  const doPassword = async () => {
    if (!form.email || !form.email.includes("@")) return setErr("Please enter a valid email address.");
    if (!form.password) return setErr("Please enter your password.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      persistEmail();
      await loginWithToken(data.token, data.user);
      nav("/app/dashboard");
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  const sendOtp = async () => {
    if (!form.email || !form.email.includes("@")) return setErr("Please enter a valid email address.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/send-otp", { email: form.email, channel: "email" });
      toast.success(data.message || "Verification code sent to your registered email.");
      setOtpSent(true);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const doOtp = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login-otp", { email: form.email, otp: form.otp });
      persistEmail();
      await loginWithToken(data.token, data.user);
      nav("/app/dashboard");
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell>
      <AuthCard>
        <div className="relative flex flex-col items-center text-center mb-6">
          <InstallAppButton className="absolute right-0 top-0" />
          <BrandIcon className="h-20 w-20" />
          <h1 className="font-serif text-3xl font-bold text-[#58111A] mt-4">Welcome Back</h1>
          <p className="text-sm text-[#605255] mt-1">Continue your journey with ManaJodi</p>
        </div>

        {err && <div className="mb-4 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-2.5" data-testid="login-error">{err}</div>}

        <div className="flex gap-1.5 mb-5 p-1 bg-[#F6EEDD] rounded-xl">
          <button onClick={() => { setMode("password"); setErr(""); }} className={`flex-1 text-sm py-2 rounded-lg font-semibold transition ${mode === "password" ? "bg-white text-[#781826] shadow-sm" : "text-[#8A6D71]"}`} data-testid="login-mode-password">Password</button>
          <button onClick={() => { setMode("otp"); setErr(""); }} className={`flex-1 text-sm py-2 rounded-lg font-semibold transition ${mode === "otp" ? "bg-white text-[#781826] shadow-sm" : "text-[#8A6D71]"}`} data-testid="login-mode-otp">Email OTP</button>
        </div>

        <label className="mj-label" htmlFor="login-email">Email Address</label>
        <div className="relative mb-3">
          <Mail className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input id="login-email" data-testid="login-email" type="email" autoComplete="email" className="mj-input pl-10" placeholder="Enter your email address" value={form.email} onChange={(e) => set("email", e.target.value.trim())} onKeyDown={(e) => e.key === "Enter" && (mode === "password" ? doPassword() : (otpSent ? doOtp() : sendOtp()))} />
        </div>

        {mode === "password" ? (
          <>
            <label className="mj-label" htmlFor="login-password">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input id="login-password" data-testid="login-password" type={showPw ? "text" : "password"} autoComplete="current-password" className="mj-input pl-10 pr-11" placeholder="Enter your password" value={form.password} onChange={(e) => set("password", e.target.value)} onKeyDown={(e) => e.key === "Enter" && doPassword()} />
              <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9E7B1B] hover:text-[#781826]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex items-center justify-between mt-3.5">
              <label className="flex items-center gap-2 text-sm text-[#605255] cursor-pointer select-none">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me" className="accent-[#781826] w-4 h-4 rounded" />
                Remember me
              </label>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-[#781826] hover:text-[#58111A]">Forgot Password?</Link>
            </div>

            <button data-testid="login-submit" onClick={doPassword} disabled={busy} className="btn-primary w-full mt-5 py-3 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Log In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </>
        ) : (
          <>
            {!otpSent ? (
              <button onClick={sendOtp} disabled={busy} className="btn-primary w-full mt-2 py-3 disabled:opacity-60" data-testid="login-send-otp">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Verification Code <ArrowRight className="w-4 h-4" /></>}</button>
            ) : (
              <>
                <label className="mj-label" htmlFor="login-otp">Verification Code</label>
                <input id="login-otp" data-testid="login-otp" className="mj-input tracking-[0.4em] text-lg text-center" placeholder="______" value={form.otp} onChange={(e) => set("otp", e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && doOtp()} />
                <div className="flex items-center justify-between mt-2">
                  <button onClick={() => setOtpSent(false)} className="text-sm text-[#605255]">Change email</button>
                  <button onClick={sendOtp} disabled={busy} className="text-sm font-semibold text-[#781826]" data-testid="login-resend-otp">Resend code</button>
                </div>
                <button data-testid="login-otp-submit" onClick={doOtp} disabled={busy} className="btn-primary w-full mt-4 py-3 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}</button>
              </>
            )}
          </>
        )}

        <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-[#EADFCF]" /><span className="text-xs text-[#9E7B1B]">or</span><div className="flex-1 h-px bg-[#EADFCF]" /></div>
        <button onClick={google} data-testid="login-google" className="btn-outline w-full py-2.5 flex items-center justify-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <p className="text-center text-sm text-[#605255] mt-6">New to ManaJodi? <Link to="/register" data-testid="create-account-link" className="text-[#781826] font-semibold">Create Account →</Link></p>
      </AuthCard>
      <p className="lg:hidden text-center text-[11px] text-[#9E7B1B] mt-6">Powered by Vijay Software Solutions Pvt Ltd</p>
    </AuthShell>
  );
}
